
// src/components/ComputeBillProfitButton.tsx
import React, { useEffect, useMemo, useState } from "react";
import { apiUrl } from "../config/api";

type OrderDetailItem = {
  id: number;
  code: string;
  name: string;
  qty: number;
  unitPrice: number;
  tiktokNetPayout: number | null;
  unitCost: number;
  profit: number; // อาจมาจาก DB (line_profit); ถ้าไม่มีจะคำนวณเองด้านล่าง
};

type OrderDetail = {
  billNo: string;
  paymentMethod: string | null;
  saleChannel: string | null;
  recDateBE: string | null;
  recTime: string | null;
  tz?: string | null;
  packageFee: number;
  shippingFee: number;
  discountAmount: number;
  createdAt: string;
  items: OrderDetailItem[];
};

const thb = (n: number) =>
  Number(n || 0).toLocaleString("th-TH", { style: "currency", currency: "THB" });

function calcLineProfit(it: OrderDetailItem): number {
  // ถ้ามี profit มาจาก DB แล้ว ใช้เลย; ไม่งั้นคำนวณ = (price - cost) * qty
  if (Number.isFinite(it.profit) && it.profit !== null) return Number(it.profit);
  const effectiveUnitPrice =
    it.tiktokNetPayout != null && Number(it.tiktokNetPayout) > 0
      ? Number(it.tiktokNetPayout)
      : Number(it.unitPrice || 0);
  return (effectiveUnitPrice - Number(it.unitCost || 0)) * Number(it.qty || 0);
}

function formatThaiShort(dt: Date): string {
  const s = new Intl.DateTimeFormat("th-TH", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(dt);
  return s + " น.";
}

type Props = {
  billNo: string;
  className?: string;
  size?: "sm" | "md";
};

export default function ProfitButton({ billNo, className, size = "sm" }: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string>("");
  const [data, setData] = useState<OrderDetail | null>(null);

  const load = async () => {
    if (!billNo) return;
    setLoading(true);
    setErr("");
    setData(null);
    try {
      const url = new URL(apiUrl("/orders-detail"));
      url.searchParams.set("bill_no", billNo);
      const resp = await fetch(url.toString());
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.error ?? resp.statusText);
      const order: OrderDetail = j.order;
      setData(order);
    } catch (e: any) {
      setErr(e?.message || "โหลดข้อมูลบิลไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) void load();
  }, [open]);

  // รวมยอดต่าง ๆ
  const summary = useMemo(() => {
    if (!data) return null;
    const revenue = data.items.reduce((sum, it) => {
      const effectiveUnitPrice =
        it.tiktokNetPayout != null && Number(it.tiktokNetPayout) > 0
          ? Number(it.tiktokNetPayout)
          : Number(it.unitPrice || 0);
      return sum + effectiveUnitPrice * it.qty;
    }, 0);
    const cogs    = data.items.reduce((sum, it) => sum + it.unitCost  * it.qty, 0);
    const gross   = revenue - cogs;
    const packageFee = Number(data.packageFee || 0);
    const shippingFee = Number(data.shippingFee || 0);
    const extraFees =
      data.saleChannel === "TIKTOK_SHOP" ? packageFee + shippingFee : 0;
    const net     = gross - (data.discountAmount || 0) - extraFees;
    return { revenue, cogs, gross, net, packageFee, shippingFee, extraFees };
  }, [data]);

  const createdStr = useMemo(() => {
    if (!data?.createdAt) return "-";
    const d = new Date(data.createdAt);
    return Number.isNaN(d.getTime()) ? "-" : formatThaiShort(d);
  }, [data?.createdAt]);

  return (
    <>
      <button
        type="button"
        className={`btn btn-outline-dark ${size === "sm" ? "btn-sm" : ""} ${className ?? ""}`}
        onClick={() => {
            const ok = window.confirm(`ต้องการดูรายละเอียดกำไรของบิล #${billNo} ใช่ไหม?`);
            if (ok) setOpen(true);
        }}
        title={`คำนวณกำไรบิล #${billNo}`}
        >
        กำไรบิล
      </button>

      {open && (
        <div
          className="modal fade show"
          role="dialog"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,.5)" }}
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content rounded-4 shadow">
              <div className="modal-header">
                <h5 className="modal-title">กำไรบิล #{billNo}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setOpen(false)} />
              </div>

              <div className="modal-body">
                {loading && <div className="small text-muted">กำลังโหลด…</div>}
                {err && <div className="alert alert-warning rounded-3">{err}</div>}

                {data && summary && (
                  <>
                    {/* สรุปบนหัว */}
                    <div className="row g-3 mb-3">
                      <div className="col">
                        <div className="text-muted">เลขที่</div>
                        <div className="fw-semibold">#{data.billNo}</div>
                        <div className="text-muted">สร้างเมื่อ: {createdStr}</div>
                        <div className="text-muted">วิธีชำระ: {data.paymentMethod ?? "-"}</div>
                        <div className="text-muted">สถานที่ขาย: {data.saleChannel ?? "-"}</div>
                      </div>
                      <div className="col-auto">
                        <div className="text-end">
                          <div>ยอดขาย (Revenue): <strong>{thb(summary.revenue)}</strong></div>
                          <div>ต้นทุน (COGS): <strong>{thb(summary.cogs)}</strong></div>
                          <div>กำไรขั้นต้น (Gross): <strong>{thb(summary.gross)}</strong></div>
                          <div className="text-danger">หักส่วนลดทั้งบิล: <strong>-{thb(data.discountAmount)}</strong></div>
                          {data.saleChannel === "TIKTOK_SHOP" && (
                            <>
                              <div className="text-danger">หัก Package Fee: <strong>-{thb(summary.packageFee)}</strong></div>
                              <div className="text-danger">หัก Shipping Fee: <strong>-{thb(summary.shippingFee)}</strong></div>
                            </>
                          )}
                          <hr className="my-2" />
                          <div className="fs-5 fw-bold">กำไรสุทธิ (Net): {thb(summary.net)}</div>
                        </div>
                      </div>
                    </div>

                    {/* ตารางแยกรายการสินค้า */}
                    <div className="table-responsive">
                      <table className="table table-sm align-middle">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 80 }}>รหัส</th>
                            <th style={{ minWidth: 280, maxWidth: 400, }}>สินค้า</th>
                            <th className="text-end" style={{ width: 80 }}>จำนวน</th>
                            <th className="text-end" style={{ width: 120 }}>ขาย/หน่วย</th>
                            <th className="text-end" style={{ width: 120 }}>ต้นทุน/หน่วย</th>
                            <th className="text-end" style={{ width: 120 }}>กำไร/หน่วย</th>
                            <th className="text-end" style={{ width: 140 }}>กำไรรวม</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.items.map((it) => {
                            const effectiveUnitPrice =
                              it.tiktokNetPayout != null && Number(it.tiktokNetPayout) > 0
                                ? Number(it.tiktokNetPayout)
                                : Number(it.unitPrice || 0);
                            const profitPerUnit = (effectiveUnitPrice - it.unitCost);
                            const profitTotal = calcLineProfit(it); // qty * (sell - cost)
                            return (
                              <tr key={it.id}>
                                <td className="text-nowrap">{it.code}</td>
                                <td className="text-start align-top" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                                    <div className="d-flex flex-column">
                                        {it.name
                                        ?.split(/\r?\n| {2,}/) // แยกบรรทัดหรือช่องว่างเยอะๆ ออกเป็นหลายบรรทัด
                                        .filter(Boolean)
                                        .map((line, i) => (
                                            <span key={i} className="text-wrap lh-sm">
                                            {line.trim()}
                                            </span>
                                        ))}
                                    </div>
                                </td>
                                <td className="text-end">{it.qty}</td>
                                <td className="text-end">{thb(effectiveUnitPrice)}</td>
                                <td className="text-end">{thb(it.unitCost)}</td>
                                <td className="text-end">{thb(profitPerUnit)}</td>
                                <td className="text-end fw-semibold">{thb(profitTotal)}</td>
                              </tr>
                            );
                          })}
                          {data.items.length === 0 && (
                            <tr>
                              <td colSpan={7} className="text-center text-muted">ไม่มีรายการสินค้า</td>
                            </tr>
                          )}
                        </tbody>
                        {data.items.length > 0 && (
                          <tfoot>
                            <tr>
                              <th colSpan={6} className="text-end">รวมกำไรรายการ</th>
                              <th className="text-end">
                                {thb(data.items.reduce((s, it) => s + calcLineProfit(it), 0))}
                              </th>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>

                    <div className="small text-muted mt-2">
                      * กำไร/หน่วย = ราคาขาย - ต้นทุน • กำไรรวม = กำไร/หน่วย × จำนวน • กำไรสุทธิ = กำไรขั้นต้น - ส่วนลดทั้งบิล {data.saleChannel === "TIKTOK_SHOP" ? "- Package Fee - Shipping Fee" : ""}
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setOpen(false)}>ปิด</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

